import {
  Col,
  Loading,
  NotAuthorized,
  NotFound,
  PageContent,
  PageHeader,
  Row,
  Tabs,
} from '@/components';
import { usePriorIdFromLineage } from '@/features/project/_hooks/project';
import { useProject } from '@/features/project/_hooks/project';
import {
  useChangeBrowserTabTitleEffect,
  useElementOffsetHeight,
  useId,
} from '@/hooks';
import {
  HEADER_ID_FOR_OFFSET_HEIGHT,
  PAGE_HEADER_PADDING,
  PS_HEADER_ID_FOR_OFFSET_HEIGHT,
  isPS,
} from '@/utils';
import type { AxiosError } from 'axios';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { ProjectsPageDynamicLink } from '../../../_components/ProjectsPageDynamicLink';
import {
  DiscriminatedInspectionEvaluationModel,
  FundSheetModel,
  ReviewSheetModel,
  SegmentSheetModel,
} from '../../../_models/project';
import { ProjectAndDiffSwitcher } from '../../_components/ProjectAndDiffSwitcher';
import { ProjectNameTitle } from '../../_components/ProjectNameTitle';
import { InspectionEvaluationDiffView } from '../_components/InspectionEvaluationDiffView';
import { KsInspectionEvaluationDiffView } from '../_components/KsInspectionEvaluationDiffView';
import { KsProjectDiffView } from '../_components/KsProjectDiffView';
import { RsProjectDiffView } from '../_components/RsProjectDiffView';
import { RsSkippedReviewProjectDiffView } from '../_components/RsSkippedReviewProjectDiffView';
import { SsInspectionEvaluationDiffView } from '../_components/SsInspectionEvaluationDiffView';
import { SsProjectDiffView } from '../_components/SsProjectDiffView';

export const ProjectDiffPage: FC = () => {
  const { t } = useTranslation();
  const { id: sourceId } = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceProject = useProject(sourceId);

  const priorId = usePriorIdFromLineage({
    params: {
      fiscalYear: sourceProject.data?.fiscalYear,
      lineageId: sourceProject.data?.lineageId ?? '',
    },
  });

  const targetId = priorId.data?.priorProjectId ?? '';
  const targetProject = useProject(targetId);
  useChangeBrowserTabTitleEffect({ title: sourceProject.data?.name });

  const headerHeight = useElementOffsetHeight({
    targetId: isPS()
      ? PS_HEADER_ID_FOR_OFFSET_HEIGHT
      : HEADER_ID_FOR_OFFSET_HEIGHT,
  });

  if (sourceProject.isError) {
    if ((sourceProject.error as AxiosError).response?.status === 403) {
      return (
        <NotAuthorized
          message={t('error-message-not-project-authorized')}
          height={`calc(100vh - ${headerHeight}px - ${PAGE_HEADER_PADDING * 2}px)`}
          backRoute="/rs/project"
        />
      );
    }
    return <NotFound />;
  }

  if (sourceProject.isPending || targetProject.isPending) {
    return <Loading />;
  }

  const sourceParsed = DiscriminatedInspectionEvaluationModel.safeParse(
    sourceProject.data,
  );
  const targetParsed = DiscriminatedInspectionEvaluationModel.safeParse(
    targetProject.data,
  );

  const activeKey = searchParams.get('activeKey') ?? 'basic-information';

  const isSourceAndTargetSuccess = sourceParsed.success && targetParsed.success;

  return (
    <>
      <PageHeader
        title={<ProjectNameTitle />}
        breadcrumbs={[
          { title: <ProjectsPageDynamicLink /> },
          {
            title: t('project-details-page'),
            href: `${location.pathname.split('/').slice(0, -1).join('/')}`,
          },
          {
            title: t('project-diff-page'),
          },
        ]}
        buttons={<ProjectAndDiffSwitcher />}
      />
      <PageContent>
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Tabs
              activeKey={activeKey}
              items={[
                {
                  key: 'basic-information',
                  label: t('basic-information'),
                  children: (() => {
                    if (!sourceParsed.success || !targetParsed.success) {
                      return null;
                    }
                    if (
                      sourceParsed.data.sheetType === 'RS' &&
                      targetParsed.data.sheetType === 'RS'
                    ) {
                      return sourceParsed.data
                        .skipAdministrativeReformTeamEvaluation &&
                        targetParsed.data
                          .skipAdministrativeReformTeamEvaluation ? (
                        <RsSkippedReviewProjectDiffView
                          sourceProject={ReviewSheetModel.parse(
                            sourceProject.data,
                          )}
                          targetProject={ReviewSheetModel.parse(
                            targetProject.data,
                          )}
                        />
                      ) : (
                        <RsProjectDiffView
                          sourceProject={ReviewSheetModel.parse(
                            sourceProject.data,
                          )}
                          targetProject={ReviewSheetModel.parse(
                            targetProject.data,
                          )}
                        />
                      );
                    }
                    if (
                      sourceParsed.data.sheetType === 'KS' &&
                      targetParsed.data.sheetType === 'KS'
                    ) {
                      return (
                        <KsProjectDiffView
                          sourceProject={FundSheetModel.parse(
                            sourceProject.data,
                          )}
                          targetProject={FundSheetModel.parse(
                            targetProject.data,
                          )}
                        />
                      );
                    }
                    if (
                      sourceParsed.data.sheetType === 'SS' &&
                      targetParsed.data.sheetType === 'SS'
                    ) {
                      return (
                        <SsProjectDiffView
                          sourceProject={SegmentSheetModel.parse(
                            sourceProject.data,
                          )}
                          targetProject={SegmentSheetModel.parse(
                            targetProject.data,
                          )}
                        />
                      );
                    }
                    return null;
                  })(),
                },

                ...(isSourceAndTargetSuccess &&
                sourceParsed.data.sheetType === 'RS' &&
                targetParsed.data.sheetType === 'RS' &&
                !sourceParsed.data.skipAdministrativeReformTeamEvaluation &&
                !targetParsed.data.skipAdministrativeReformTeamEvaluation
                  ? [
                      {
                        key: 'inspection-evaluation',
                        label: t('inspection-evaluation'),
                        children: (
                          <InspectionEvaluationDiffView
                            sourceInspectionEvaluation={
                              sourceParsed.data.inspectionEvaluation
                            }
                            targetInspectionEvaluation={
                              targetParsed.data.inspectionEvaluation
                            }
                          />
                        ),
                      },
                    ]
                  : []),
                ...(isSourceAndTargetSuccess &&
                sourceParsed.data.sheetType === 'SS' &&
                targetParsed.data.sheetType === 'SS'
                  ? [
                      {
                        key: 'inspection-evaluation',
                        label: t('inspection-evaluation'),
                        children: (
                          <SsInspectionEvaluationDiffView
                            sourceInspectionEvaluation={
                              sourceParsed.data.inspectionEvaluation
                            }
                            targetInspectionEvaluation={
                              targetParsed.data.inspectionEvaluation
                            }
                          />
                        ),
                      },
                    ]
                  : []),
                ...(isSourceAndTargetSuccess &&
                sourceParsed.data.sheetType === 'KS' &&
                targetParsed.data.sheetType === 'KS' &&
                sourceProject.data?.fundAdditionalAttributes?.fundSheetType ===
                  'standard' &&
                targetProject.data?.fundAdditionalAttributes?.fundSheetType ===
                  'standard'
                  ? [
                      {
                        key: 'inspection-evaluation',
                        label: t('inspection-evaluation'),
                        children: (
                          <KsInspectionEvaluationDiffView
                            sourceFiscalYear={
                              FundSheetModel.parse(sourceProject.data)
                                .fiscalYear
                            }
                            targetFiscalYear={
                              FundSheetModel.parse(targetProject.data)
                                .fiscalYear
                            }
                            sourceInspectionEvaluation={
                              sourceParsed.data.inspectionEvaluation
                            }
                            targetInspectionEvaluation={
                              targetParsed.data.inspectionEvaluation
                            }
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
              onChange={(activeKey) => {
                searchParams.set('activeKey', activeKey);
                setSearchParams(searchParams, { replace: true });
              }}
            />
          </Col>
        </Row>
      </PageContent>
    </>
  );
};
